/**
 * DesignerScreen — Squad Designer (F2-FE-1).
 *
 * Porta a `DesignerScreen` do protótipo NavaFlowPrototype.jsx (Fase 4 · Definição
 * dos Squads). Em F2 é primariamente visualização do grafo montado pelo organizer,
 * lendo GET /executions/{id}/squad.
 *
 * Layout fiel ao protótipo:
 *   Esquerda (260px):  Painel de info do squad (cor, nome, missão, estimativa)
 *   Centro:            Canvas SVG com nós e arestas
 *   Direita (300px):   Painel de detalhe do nó selecionado (NODE CONFIG)
 *
 * Toolbar superior: badge "SUGERIDO · ORGANIZER", estimativa e botão
 * "Submeter ao Gate A" (quando status permite).
 *
 * Acessibilidade:
 *   - Nós navegáveis por teclado (tabIndex, role="button")
 *   - Foco visível (outline cyber)
 *   - prefers-reduced-motion: remove animação navaPulse em nós
 *   - aria-labels nos ícones decorativos
 *
 * Standards: Clean (sem lógica de negócio misturada), NFR (a11y), OWASP (auth
 * validado pelo back; front apenas oculta o botão de submissão quando não há gate).
 */

import { useState, useMemo, useCallback } from 'react';
import {
  GitBranch, Cpu, ArrowRight, CheckCircle, Clock, Zap, AlertCircle,
  ChevronRight, Layers, Shield, Info, BarChart2,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, ForbiddenBanner } from '@/components/ui';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { TEXTO_DO_SELO_PROMPT, rotuloDaEtapa } from '@/lib/agentesDaExecucao';
import { useExecutionSquad, useExecution, useResuggestSquad, useStartExecution, useEditSquadNode } from '@/api/executions';
import { useIniciarAguardandoIndexacao } from '@/api/aguardarIndexacao';
import { AvisoIndexacao } from '@/components/AvisoIndexacao';
import { useAgents } from '@/api/agents';
import { useProjects } from '@/api/projects';
import { useNavigationContext } from '@/navigation';

/** Paleta de tipos de nó (escopo: Input, LLM Agent, Condition, Transform, Output). */
const NODE_TYPE_PALETTE = [
  { key: 'input',     label: 'Input',     desc: 'Entrada do fluxo',        color: C.neutralDim, Icon: GitBranch },
  { key: 'llm_agent', label: 'LLM Agent', desc: 'Agente com modelo LLM',   color: C.cyber,      Icon: Cpu },
  { key: 'condition', label: 'Condition', desc: 'Ramificação condicional', color: C.neon,       Icon: ChevronRight },
  { key: 'transform', label: 'Transform', desc: 'Transformação de dados',  color: C.digital,    Icon: Layers },
  { key: 'output',    label: 'Output',    desc: 'Pull Request final',      color: C.quantum,    Icon: ArrowRight },
];

// ─── Constantes ───────────────────────────────────────────────────────────────

/** Mapeamento de tier → cor da paleta C */
const TIER_COLOR = {
  opus:   C.quantum,
  sonnet: C.cyber,
  haiku:  C.neon,
};

/** Mapeamento de node_type → ícone e cor */
const NODE_TYPE_META = {
  input:      { label: 'Input',       color: C.neutralDim,   Icon: GitBranch },
  llm_agent:  { label: 'LLM Agent',   color: C.cyber,        Icon: Cpu       },
  condition:  { label: 'Condição',    color: C.neon,         Icon: ChevronRight },
  transform:  { label: 'Transform',   color: C.digital,      Icon: Layers    },
  output:     { label: 'Output',      color: C.quantum,      Icon: ArrowRight },
};

/** Largura/altura padrão de um nó no canvas */
const NODE_W = 200;
const NODE_H = 72;

// ─── Sub-componentes ──────────────────────────────────────────────────────────

/** Pill de estimativa (tokens / custo) */
function EstimatePill({ label, value, color = C.neutralDim }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, color: C.neutralMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color }}>
        {value}
      </span>
    </div>
  );
}

/** Chip de Engineering Standard */
function StandardChip({ code }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: 3,
        backgroundColor: `${C.cyber}18`,
        border: `1px solid ${C.cyber}33`,
        color: C.cyber,
        letterSpacing: '0.03em',
      }}
    >
      {code}
    </span>
  );
}

/** Nó individual no canvas SVG (renderizado como foreignObject) */
function GraphNode({ node, isSelected, onSelect }) {
  const meta = NODE_TYPE_META[node.node_type] ?? NODE_TYPE_META.llm_agent;
  // TODOS os agentes usam o MELHOR modelo (opus) — a plataforma força; exibimos opus
  // (antes mostrava o tier salvo, ex.: sonnet, o que confundia).
  // A3: com o elenco congelado a API manda o tier EFETIVO (e etapas/prompt) — aí ele vale.
  const temA3 = Array.isArray(node.etapas) || Boolean(node.prompt);
  const displayTier = temA3 ? (node.tier || null) : (node.tier ? 'opus' : null);
  const tierColor = TIER_COLOR[temA3 ? node.tier : 'opus'] ?? C.neutralDim;
  const etapasDoNo = Array.isArray(node.etapas) ? node.etapas : [];
  const seloDoNo = node.prompt?.selo;

  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(node); } },
    [node, onSelect],
  );

  return (
    <foreignObject
      x={node.position_x}
      y={node.position_y}
      width={NODE_W}
      height={NODE_H}
    >
      <div
        tabIndex={0}
        role="button"
        aria-label={`Nó: ${node.node_label}${displayTier ? ` · ${displayTier}` : ''}`}
        aria-pressed={isSelected}
        onClick={() => onSelect(node)}
        onKeyDown={handleKeyDown}
        style={{
          width: NODE_W - 2,
          height: NODE_H - 2,
          backgroundColor: isSelected ? `${meta.color}22` : C.blackElev2,
          border: `1px solid ${isSelected ? meta.color : C.blackBorder}`,
          borderRadius: 8,
          padding: '10px 12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          outline: 'none',
          transition: 'border-color 150ms',
        }}
        className="focus-visible:outline focus-visible:outline-2"
      >
        {/* Linha superior: ícone + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <meta.Icon size={13} aria-hidden="true" style={{ color: meta.color, flexShrink: 0 }} />
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: C.neutral,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {node.node_label}
          </span>
        </div>
        {/* Linha inferior: agent_id; tier efetivo, selo e etapas só quando a API manda (A3) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {temA3 && seloDoNo && (
            <span
              data-testid={`selo-no-${node.node_id}`}
              title={TEXTO_DO_SELO_PROMPT[seloDoNo] ?? seloDoNo}
              style={{ fontSize: 11 }}
            >
              {seloDoNo === 'especialista' ? '✅' : '⚠️'}
            </span>
          )}
          {temA3 && etapasDoNo.length > 0 && (
            <span
              title={etapasDoNo.map(rotuloDaEtapa).join(', ')}
              style={{ fontSize: 10, color: C.neutralDim, whiteSpace: 'nowrap' }}
            >
              {etapasDoNo.length} etapa(s)
            </span>
          )}
          {temA3 && displayTier && (
            <span style={{
              fontSize: 10, fontWeight: 600, color: tierColor, padding: '1px 5px',
              borderRadius: 3, backgroundColor: `${tierColor}18`, border: `1px solid ${tierColor}33`,
              textTransform: 'uppercase',
            }}>{displayTier}</span>
          )}
          {node.agent_id && (
            <span style={{
              fontSize: 10,
              color: C.neutralMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {node.agent_id}
            </span>
          )}
        </div>
      </div>
    </foreignObject>
  );
}

/** Aresta SVG entre dois nós */
function GraphEdge({ edge, nodes }) {
  const srcNode = nodes.find((n) => n.node_id === edge.source_node);
  const tgtNode = nodes.find((n) => n.node_id === edge.target_node);
  if (!srcNode || !tgtNode) return null;

  const x1 = srcNode.position_x + NODE_W;
  const y1 = srcNode.position_y + NODE_H / 2;
  const x2 = tgtNode.position_x;
  const y2 = tgtNode.position_y + NODE_H / 2;

  // Bezier horizontal para dar curva suave
  const cx1 = x1 + (x2 - x1) * 0.4;
  const cx2 = x2 - (x2 - x1) * 0.4;

  const edgeColor = edge.edge_type === 'conditional' ? C.neon : C.blackBorder;

  return (
    <g>
      <path
        d={`M ${x1} ${y1} C ${cx1} ${y1}, ${cx2} ${y2}, ${x2} ${y2}`}
        fill="none"
        stroke={edgeColor}
        strokeWidth={1.5}
        strokeDasharray={edge.edge_type === 'conditional' ? '4 3' : 'none'}
      />
      {/* Ponta de seta */}
      <polygon
        points={`${x2},${y2} ${x2 - 7},${y2 - 4} ${x2 - 7},${y2 + 4}`}
        fill={edgeColor}
      />
      {/* Label da aresta */}
      {edge.edge_label && (
        <text
          x={(x1 + x2) / 2}
          y={(y1 + y2) / 2 - 6}
          textAnchor="middle"
          fontSize={9}
          fill={C.neutralMuted}
          style={{ fontFamily: 'inherit' }}
        >
          {edge.edge_label}
        </text>
      )}
    </g>
  );
}

/** Painel do nó — SÓ o nome do agente. Única ação permitida: TROCAR de agente. */
function NodeConfigPanel({ node, onClose, executionId }) {
  const edit = useEditSquadNode(executionId);
  const { data: agentsPage } = useAgents({ limit: 500 });
  const agents = useMemo(
    () => (agentsPage?.items ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [agentsPage],
  );
  const isAgent = node.node_type === 'agent';
  const canSwap = isAgent && Boolean(executionId);
  const currentInList = agents.some((a) => a.slug === node.agent_id);

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.neutralMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          AGENTE
        </span>
        <button onClick={onClose} aria-label="Fechar painel"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.neutralMuted, fontSize: 16, lineHeight: 1 }}>×</button>
      </div>

      {/* SÓ o nome do agente */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.neutral }}>{node.node_label}</div>
      </div>

      {/* Única ação: TROCAR de agente */}
      {isAgent && (
        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Trocar de agente</FieldLabel>
          {canSwap ? (
            <>
              <select
                value={node.agent_id ?? ''}
                disabled={edit.isPending}
                onChange={(e) => {
                  const slug = e.target.value;
                  const a = agents.find((x) => x.slug === slug);
                  edit.mutate({ nodeId: node.node_id, agent_id: slug, node_label: a?.name ?? node.node_label });
                }}
                style={{ width: '100%', background: C.blackElev2, color: C.neutral,
                  border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: '8px 9px', fontSize: 13 }}>
                {!currentInList && node.agent_id && (
                  <option value={node.agent_id}>{node.node_label} (atual)</option>
                )}
                {agents.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
              </select>
              {edit.isPending && <div style={{ fontSize: 12, color: C.neutralMuted, marginTop: 6 }}>Trocando…</div>}
              {edit.isError && <div style={{ fontSize: 12, color: C.hot, marginTop: 6 }}>Falha ao trocar de agente.</div>}
            </>
          ) : (
            <span style={{ fontSize: 12.5, color: C.neutralMuted }}>Disponível durante a execução.</span>
          )}
          <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 8, lineHeight: 1.5 }}>
            Modelo (<strong>opus</strong>) e tokens são definidos pela plataforma. Aqui você só troca o agente.
          </div>
        </div>
      )}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{
      fontSize: 10,
      fontWeight: 600,
      color: C.neutralMuted,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      marginBottom: 4,
    }}>
      {children}
    </div>
  );
}

/** Painel esquerdo de info do squad */
function SquadInfoPanel({ squad, estimate }) {
  const colorMap = {
    quantum: C.quantum,
    cyber:   C.cyber,
    neon:    C.neon,
    digital: C.digital,
    hot:     C.hot,
  };
  const accentColor = colorMap[squad.color_token] ?? C.cyber;

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: accentColor,
            flexShrink: 0,
          }} aria-hidden="true" />
          <span style={{ fontSize: 11, fontWeight: 600, color: C.neutralMuted, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
            Squad
          </span>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: C.neutral, marginBottom: 6 }}>
          {squad.squad_name}
        </div>
        {squad.mission && (
          <div style={{ fontSize: 12, color: C.neutralDim, lineHeight: 1.6 }}>
            {squad.mission}
          </div>
        )}
      </div>

      {/* Estimativa */}
      {estimate && (
        <Card style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.neutralMuted, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>
            Estimativa
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <EstimatePill
              label="Tokens"
              value={estimate.total_tokens != null ? estimate.total_tokens.toLocaleString('pt-BR') : '—'}
              color={C.cyber}
            />
            <EstimatePill
              label="Custo"
              value={estimate.total_cost_usd != null ? `US$ ${Number(estimate.total_cost_usd).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
              color={C.neon}
            />
          </div>
        </Card>
      )}

      {/* Legenda de tipos */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: C.neutralMuted, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>
          Legenda
        </div>
        {Object.entries(NODE_TYPE_META).map(([key, meta]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <meta.Icon size={12} aria-hidden="true" style={{ color: meta.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: C.neutralDim }}>{meta.label}</span>
          </div>
        ))}
      </div>

      {/* Tier legend */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: C.neutralMuted, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>
          Modelos
        </div>
        {Object.entries(TIER_COLOR).map(([tier, color]) => (
          <div key={tier} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color,
              flexShrink: 0,
            }} aria-hidden="true" />
            <span style={{ fontSize: 11, color: C.neutralDim, textTransform: 'capitalize' }}>{tier}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Estado vazio — squad ainda não montado */
function EmptySquad() {
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      padding: 48,
    }}>
      <Layers size={40} aria-hidden="true" style={{ color: C.blackBorder }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: C.neutralMuted }}>
        Squad ainda não montado
      </div>
      <div style={{ fontSize: 13, color: C.neutralMuted, textAlign: 'center', maxWidth: 320 }}>
        O organizer ainda não compôs o grafo do squad para esta execução.
        Aguarde a conclusão das fases anteriores.
      </div>
    </div>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

/**
 * DesignerScreen — Squad Designer (F2-FE-1).
 *
 * Lê o executionId de ?execution=<id> na query string (shim compatível com o
 * protótipo onde não havia rota dinâmica — extensível para /journey/designer/:id
 * em ondas futuras).
 */
export function DesignerScreen() {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const urlProject = searchParams.get('project');

  const { navigate } = useNavigationContext();

  // Sem execução: permitir INICIAR uma (gera o squad) escolhendo um projeto.
  const { data: projectsRaw } = useProjects();
  const projects = Array.isArray(projectsRaw) ? projectsRaw : (projectsRaw?.items ?? []);
  const startExec = useStartExecution();
  // 409 "contexto não indexado": espera visível + repetição automática.
  const { iniciar: iniciarExec, estado: indexacao } =
    useIniciarAguardandoIndexacao(startExec.mutateAsync);
  const [pickProject, setPickProject] = useState('');

  const {
    data: squad,
    isLoading: squadLoading,
    error: squadError,
  } = useExecutionSquad(executionId);

  const {
    data: execution,
  } = useExecution(executionId);

  const [selectedNode, setSelectedNode] = useState(null);
  const resuggest = useResuggestSquad(executionId);

  // ── Cálculo do viewport SVG ────────────────────────────────────────────────
  const svgBounds = useMemo(() => {
    if (!squad?.nodes?.length) return { width: 800, height: 500 };
    const maxX = Math.max(...squad.nodes.map((n) => n.position_x + NODE_W));
    const maxY = Math.max(...squad.nodes.map((n) => n.position_y + NODE_H));
    return { width: maxX + 60, height: maxY + 60 };
  }, [squad]);

  const handleSelectNode = useCallback((node) => {
    setSelectedNode((prev) => (prev?.node_id === node.node_id ? null : node));
  }, []);

  const handleSubmitGateA = useCallback(() => {
    if (executionId) {
      navigate(`gates`);
    }
  }, [executionId, navigate]);

  // ── Guards de estado ──────────────────────────────────────────────────────
  if (!executionId) {
    const effProject = pickProject || urlProject || projects?.[0]?.id || '';
    const launch = async () => {
      if (!effProject) return;
      try {
        const res = await iniciarExec(effProject);
        const eid = res?.execution_id;
        if (eid) navigate(`/journey/designer?execution=${eid}`);
      } catch { /* erro exibido abaixo */ }
    };
    return (
      <div style={{ padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <Info size={36} aria-hidden="true" style={{ color: C.neutralMuted }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>
          Nenhuma execução ainda — inicie uma para gerar o squad
        </div>
        <div style={{ fontSize: 13, color: C.neutralMuted, maxWidth: 520, textAlign: 'center' }}>
          O squad é montado pelo pipeline (passo <strong>organize_squad</strong>) ao iniciar a execução.
          Escolha o projeto e inicie — o grafo do squad aparece aqui automaticamente quando ficar pronto.
        </div>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
          <select value={effProject} onChange={(e) => setPickProject(e.target.value)}
            style={{ padding: '9px 12px', background: C.blackElev, color: C.neutral,
              border: `1px solid ${C.blackBorder}`, borderRadius: 8, fontSize: 13, minWidth: 240 }}>
            {projects.length === 0 && <option value="">(nenhum projeto)</option>}
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Btn variant="primary" onClick={launch} size="md"
            disabled={!effProject || startExec.isPending || indexacao.fase === 'indexando'}
            aria-busy={startExec.isPending || indexacao.fase === 'indexando'}>
            {indexacao.fase === 'indexando' ? 'Aguardando indexação…'
              : startExec.isPending ? 'Iniciando…' : '▶ Iniciar execução (gera o squad)'}
          </Btn>
        </div>
        <AvisoIndexacao estado={indexacao} onTentarDeNovo={launch} />
        {startExec.isError && indexacao.fase === 'ocioso' && (
          <div style={{ fontSize: 12.5, color: C.hot }}>
            Falha ao iniciar a execução: {String(startExec.error?.title || startExec.error?.detail || startExec.error?.message || startExec.error)}
          </div>
        )}
        <Btn variant="ghost" onClick={() => navigate('dashboard')} size="sm">
          Ir para o Dashboard
        </Btn>
      </div>
    );
  }

  if (squadLoading) {
    return <LoadingSpinner label="Carregando grafo do squad..." />;
  }

  if (squadError) {
    if (squadError.isForbidden) return <ForbiddenBanner detail={squadError.detail} />;
    return <QueryError error={squadError} />;
  }

  // squad_id === null → squad ainda não montado (resposta coerente do back)
  const squadNotReady = !squad || !squad.squad_id;

  // ── Barra de status da execução ───────────────────────────────────────────
  const canSubmit = execution?.status === 'squad_ready' || execution?.status === 'designer_open';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      backgroundColor: C.black,
    }}>
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 20px',
        borderBottom: `1px solid ${C.blackBorder}`,
        backgroundColor: C.blackElev,
        flexShrink: 0,
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Cpu size={15} aria-hidden="true" style={{ color: C.cyber }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: C.neutral }}>
              Squad Designer
            </span>
          </div>
          <Badge color={C.cyber} light>SUGERIDO · ORGANIZER</Badge>
          {squad?.squad_name && (
            <span style={{ fontSize: 13, color: C.neutralDim }}>
              {squad.squad_name}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Estimativa inline na toolbar */}
          {squad?.estimate && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <EstimatePill
                label="Est. Tokens"
                value={squad.estimate.total_tokens != null
                  ? squad.estimate.total_tokens.toLocaleString('pt-BR')
                  : '—'}
                color={C.cyber}
              />
              <EstimatePill
                label="Est. Custo"
                value={squad.estimate.total_cost_usd != null
                  ? `US$ ${Number(squad.estimate.total_cost_usd).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : '—'}
                color={C.neon}
              />
            </div>
          )}

          <Btn
            variant="ghost"
            size="sm"
            disabled={resuggest.isPending}
            onClick={() => { setSelectedNode(null); resuggest.mutate(); }}
            icon={Zap}
            aria-label="Re-sugerir squads via organizer"
          >
            {resuggest.isPending ? 'Re-sugerindo…' : 'Re-sugerir squads'}
          </Btn>

          <Btn
            variant="primary"
            size="sm"
            disabled={!canSubmit || squadNotReady}
            onClick={handleSubmitGateA}
            icon={CheckCircle}
            aria-label="Submeter ao Gate A"
          >
            Submeter ao Gate A
          </Btn>
        </div>
      </div>

      {/* ── Corpo principal em 3 colunas ────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* Coluna esquerda — info do squad */}
        <div style={{
          width: 260,
          flexShrink: 0,
          borderRight: `1px solid ${C.blackBorder}`,
          padding: 16,
          overflow: 'auto',
          backgroundColor: C.blackElev,
        }}>
          {squadNotReady
            ? (
              <div style={{ fontSize: 13, color: C.neutralMuted }}>
                Squad não montado ainda.
              </div>
            )
            : (
              <SquadInfoPanel
                squad={squad}
                estimate={squad.estimate}
              />
            )}

          {resuggest.isError && (
            <div style={{ marginTop: 12, fontSize: 12, color: C.digital }}>
              Falha ao re-sugerir: {String(resuggest.error?.detail || resuggest.error?.message || 'erro')}
            </div>
          )}

          {/* Node Types — paleta de tipos de nó (escopo) */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.blackBorder}` }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.neutralMuted,
              letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>
              Node Types
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {NODE_TYPE_PALETTE.map(({ key, label, desc, color, Icon }) => (
                <div
                  key={key}
                  draggable
                  title={`${label} — ${desc}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                    border: `1px solid ${C.blackBorder}`, borderRadius: 8, background: C.black,
                    cursor: 'grab' }}
                >
                  <span style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                    background: `${color}22`, display: 'grid', placeItems: 'center' }}>
                    <Icon size={14} aria-hidden="true" style={{ color }} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: C.neutral }}>{label}</div>
                    <div style={{ fontSize: 10.5, color: C.neutralMuted, whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis' }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Centro — canvas */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          backgroundColor: C.black,
        }}>
          {squadNotReady
            ? <EmptySquad />
            : (
              <svg
                role="img"
                aria-label={`Grafo do squad ${squad.squad_name}: ${squad.nodes.length} nós`}
                width={svgBounds.width}
                height={svgBounds.height}
                style={{ display: 'block', minWidth: '100%' }}
              >
                {/* Grade de pontos de fundo */}
                <defs>
                  <pattern id="designer-dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="0.8" fill={C.blackBorder} />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#designer-dots)" />

                {/* Arestas (abaixo dos nós) */}
                {squad.edges?.map((edge) => (
                  <GraphEdge
                    key={edge.edge_id}
                    edge={edge}
                    nodes={squad.nodes}
                  />
                ))}

                {/* Nós */}
                {squad.nodes.map((node) => (
                  <GraphNode
                    key={node.node_id}
                    node={node}
                    isSelected={selectedNode?.node_id === node.node_id}
                    onSelect={handleSelectNode}
                  />
                ))}
              </svg>
            )}
        </div>

        {/* Coluna direita — NODE CONFIG */}
        {selectedNode && (
          <div style={{
            width: 300,
            flexShrink: 0,
            borderLeft: `1px solid ${C.blackBorder}`,
            padding: 16,
            overflow: 'auto',
            backgroundColor: C.blackElev,
          }}>
            <NodeConfigPanel
              node={selectedNode}
              onClose={() => setSelectedNode(null)}
              executionId={executionId}
            />
          </div>
        )}
      </div>
    </div>
  );
}
